import { Check } from "lucide-react";

import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@design-lab/shared/ui/tabs";
import { Header } from "@design-lab/widgets/navigation";
import { TransfersLabArtboard } from "@design-lab/widgets/artboard/ui/TransfersLabArtboard";

import { type AppTab } from "@design-lab/entities/transfers-lab/model/useTab";
import {
  type TransfersTab,
  useTransfersLabDeliveryEstimates
} from "@design-lab/entities/transfers-lab";
import { TransfersLabArtworksUpload } from "@design-lab/widgets/transfers-lab-artworks-upload";
import { TransfersImageReview } from "@design-lab/widgets/transfers-image-review";
import { TransfersSizes } from "@design-lab/widgets/transfers-sizes";
import { TransfersFooter } from "@design-lab/widgets/transfers-footer";

export type TransfersLabDesktopProps = {
  handleTabClick: (value: TransfersTab) => void;
  tabs: AppTab[];
  tab: TransfersTab;
  // Footer props - these should come from your state management
  footerProps?: {
    unitPrice: number;
    totalPrice: number;
    quantity: number;
    showSaveOptions?: boolean;
    preCutPrice?: number;
    isPreCutEnabled?: boolean;
    onPreCutToggle?: (enabled: boolean) => void;
    onAddToCart: () => void;
    isAddingToCart?: boolean;
    isAddToCartDisabled?: boolean;
  };
};

export function TransfersLabDesktop({
  tabs,
  tab,
  handleTabClick,
  footerProps = {
    // Default mock data - replace with real data from state
    unitPrice: 14.12,
    totalPrice: 122.73,
    quantity: 70,
    showSaveOptions: true,
    preCutPrice: 0.3,
    isPreCutEnabled: true,
    onPreCutToggle: enabled => console.log("Pre-cut enabled:", enabled),
    onAddToCart: () => console.log("Add to cart clicked"),
    isAddingToCart: false,
    isAddToCartDisabled: false
  }
}: TransfersLabDesktopProps) {
  const deliveryDateInfo = useTransfersLabDeliveryEstimates();

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gray-100">
      <Header deliveryDateInfo={deliveryDateInfo} />
      <div className="flex flex-1 gap-4 overflow-hidden p-6">
        <Tabs value={tab} className="flex flex-1 flex-row items-start gap-4">
          <TabsList className="flex-0 h-auto flex-col gap-2">
            {tabs.map((item, index) => (
              <TabsTrigger
                data-testid={`tab-${item.value}-trigger`}
                key={item.title}
                value={item.value}
                asChild
                disabled={!item.enabled}
              >
                <Button
                  data-testid={`tab-${item.value}-button`}
                  variant="secondary"
                  isDisabled={!item.enabled}
                  onPress={() => handleTabClick(item.value)}
                  className="bg-background border-3 relative h-auto min-h-20 w-full flex-col gap-2 rounded-2xl px-3 text-sm shadow-md outline outline-offset-[-1px] outline-gray-200 group-data-[selected]:border-blue-600 group-data-[selected]:text-blue-600"
                >
                  {item.icon}
                  {item.enabled && item.checked && (
                    <div className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full bg-blue-600">
                      <Check className="text-background h-3 w-3" />
                    </div>
                  )}
                  <span>
                    {index + 1}. {item.title}
                  </span>
                </Button>
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="h-full flex-1 overflow-hidden">
            <TabsContent value="upload" className="mt-0 h-full">
              <TransfersLabArtworksUpload />
            </TabsContent>
            <TabsContent value="review" className="mt-0 h-full">
              <TransfersImageReview />
            </TabsContent>
            <TabsContent value="sizes" className="mt-0 h-full">
              <TransfersSizes />
            </TabsContent>
          </div>
        </Tabs>

        {/* Artboard for transfers lab */}
        <div className="flex-1">
          <TransfersLabArtboard />
        </div>
      </div>

      <TransfersFooter {...footerProps} />
    </div>
  );
}
